/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { BarChart3, BookOpen, FileText, HardDrive, Languages, LayoutDashboard, LayoutTemplate, Network, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

interface ToolConfig {
    description: string;
    icon: LucideIcon;
    label: string;
    route: string;
}

const TOOLS: ToolConfig[] = [
    {
        description: 'Learn how to call the central EE-Check API client from project code.',
        icon: BookOpen,
        label: 'API',
        route: APP_ROUTES.api,
    },
    {
        description: 'Browse example dashboard layouts and their responsive behavior.',
        icon: LayoutDashboard,
        label: 'Example Dashboards',
        route: APP_ROUTES.exampledashboards,
    },
    {
        description: 'Generate virtual device definitions for a selected customer and group.',
        icon: FileText,
        label: 'Device Definitions',
        route: APP_ROUTES.devicedefinitions,
    },
    {
        description: 'Browse the application structure and its main components.',
        icon: Network,
        label: 'Component Overview',
        route: APP_ROUTES.componentoverview,
    },
    {
        description: 'Browse all available KPI definitions and example sensor names.',
        icon: BarChart3,
        label: 'KPIs',
        route: APP_ROUTES.kpis,
    },
    {
        description: 'Browse UI layout components and implementation examples.',
        icon: LayoutTemplate,
        label: 'Layouts',
        route: APP_ROUTES.layouts,
    },
    {
        description: 'Browse configured devices and their available sensors.',
        icon: HardDrive,
        label: 'Sensors / Devices',
        route: APP_ROUTES.sensorsdevices,
    },
    {
        description: 'Browse all project translation keys and their German and Spanish values.',
        icon: Languages,
        label: 'Translations',
        route: APP_ROUTES.translations,
    },
];

function ToolsTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6" aria-label={t('Tools')}>
            <section className="flex flex-col gap-2">
                <h2>{t('Tools')}</h2>
                <p>{t('Select a tool to explore the available reference pages and utilities:')}</p>
            </section>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {TOOLS.map((tool) => {
                    const Icon = tool.icon;

                    return (
                        <NavLink
                            key={tool.route}
                            to={tool.route}
                            className="dashboard-panel interactive-surface !min-h-44 no-underline transition-[background,transform] duration-150"
                        >
                            <div className="flex h-full w-full flex-col items-start justify-center gap-3 px-6 text-left">
                                <div className="flex w-full items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-base-content/75" aria-hidden="true" />
                                        <span className="text-xl font-semibold">{t(tool.label)}</span>
                                    </div>
                                    <span className="badge badge-secondary shrink-0">{t('Tools')}</span>
                                </div>
                                <span className="text-sm text-base-content/70">
                                    {t(tool.description)}
                                </span>
                            </div>
                        </NavLink>
                    );
                })}
            </section>
        </div>
    );
}

export default ToolsTab;
